const INLINE = ["strong", "em", "u", "s", "a", "br"];
const PROFILES = {
  inline: new Set(INLINE),
  lines: new Set([...INLINE, "p"]),
  basic: new Set([...INLINE, "p", "ul", "ol", "li"]),
  full: new Set([...INLINE, "p", "ul", "ol", "li", "h2", "h3", "h4", "blockquote", "hr", "img", "pre", "code"]),
};
const ALIASES = { b: "strong", i: "em", strike: "s", del: "s", h1: "h2", h5: "h4", h6: "h4" };
const DROP = new Set(["script", "style", "iframe", "object", "embed", "noscript", "template", "svg", "math", "head", "title", "meta", "link"]);
const BLOCK = new Set([
  "p", "div", "li", "ul", "ol", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre",
  "section", "article", "header", "footer", "table", "tr", "hr",
]);
const escapeText = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\u00a0/g, " ");
const escapeAttr = (s) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function safeUrl(value, { allowRelative }) {
  const url = String(value || "").trim();
  const compact = url.replace(/[\u0000-\u0020\u007f-\u009f]/g, "");
  const okProtocol = /^(https?:|mailto:|tel:)/i.test(compact);
  const okRelative = allowRelative && (/^\/(?!\/)/.test(compact) || /^#/.test(compact));
  return okProtocol || okRelative ? url : "";
}
function serializeChildren(node, allowed) {
  let out = "";
  node.childNodes.forEach((child) => {
    out += serializeNode(child, allowed);
  });
  return out;
}
function serializeNode(node, allowed) {
  if (node.nodeType === 3) return escapeText(node.nodeValue || "");
  if (node.nodeType !== 1) return "";

  let name = node.nodeName.toLowerCase();
  if (DROP.has(name)) return "";
  name = ALIASES[name] || name;
  if (name === "div" && allowed.has("p")) name = "p";
  if (!allowed.has(name)) {
    const text = serializeChildren(node, allowed);
    if (BLOCK.has(name)) return allowed.has("br") ? `${text}<br>` : `${text} `;
    return text;
  }
  switch (name) {
    case "br":
      return "<br>";
    case "hr":
      return "<hr>";
    case "img": {
      const src = safeUrl(node.getAttribute("src"), { allowRelative: false });
      if (!/^https?:/i.test(src)) return "";
      return `<img src="${escapeAttr(src)}" alt="${escapeAttr(node.getAttribute("alt") || "")}" loading="lazy">`;
    }
    case "a": {
      const href = safeUrl(node.getAttribute("href"), { allowRelative: true });
      const inner = serializeChildren(node, allowed);
      return href
        ? `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`
        : inner;
    }
    default:
      return `<${name}>${serializeChildren(node, allowed)}</${name}>`;
  }
}
function tidy(html) {
  let out = html;
  let prev;
  do {
    prev = out;
    out = out
      .replace(/<(strong|em|u|s)>\s*<\/\1>/g, "")
      .replace(/<p>\s*(?:<br>\s*)*<\/p>/g, "");
  } while (out !== prev);
  return out.replace(/^(?:\s*<br>)+/, "").replace(/(?:<br>\s*)+$/, "").trim();
}
function parse(html) {
  return new DOMParser().parseFromString(String(html || ""), "text/html").body;
}
export function sanitizeHtml(html, profile = "basic") {
  if (html === undefined || html === null || html === "") return "";
  const allowed = PROFILES[profile] || PROFILES.basic;
  return tidy(serializeChildren(parse(html), allowed));
}
export function isEmptyHtml(html) {
  if (!html) return true;
  const body = parse(html);
  const text = (body.textContent || "").replace(/\u00a0/g, " ").trim();
  return !text && !body.querySelector("img,hr");
}
export function htmlToLines(html) {
  if (!html) return [];
  const lines = [];
  let buf = "";
  const flush = () => {
    const line = tidy(buf);
    if (!isEmptyHtml(line)) lines.push(line);
    buf = "";
  };
  const walk = (parent) => {
    parent.childNodes.forEach((child) => {
      const name = child.nodeName.toLowerCase();
      if (child.nodeType === 1 && name === "br") {
        flush();
      } else if (child.nodeType === 1 && BLOCK.has(name)) {
        flush();
        walk(child);
        flush();
      } else {
        buf += serializeNode(child, PROFILES.inline);
      }
    });
  };
  walk(parse(html));
  flush();
  return lines;
}
export function linesToHtml(lines) {
  const list = Array.isArray(lines) ? lines : [];
  return list
    .filter((l) => typeof l === "string" && l.trim() !== "")
    .map((l) => `<p>${sanitizeHtml(l, "inline")}</p>`)
    .join("");
}